import { mountPlayer } from './app.js';

export class BizmuthPlayer extends HTMLElement {
  static observedAttributes = ['config'];
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.revision = 0;
  }
  connectedCallback() { this.ready = this.render(); }
  attributeChangedCallback(name, previous, value) {
    if (previous !== value && this.isConnected) this.ready = this.render();
  }
  disconnectedCallback() {
    this.revision++;
    this.download?.abort();
    this.cleanup = this.controller?.dispose();
    this.controller = null;
  }
  async start(machine) {
    if (!this.isConnected) throw new Error('Connect the player element before starting it');
    const revision = this.revision;
    await this.ready;
    if (!this.isConnected || revision !== this.revision) throw new Error('Player configuration changed before startup');
    if (!this.controller) throw this.initializationError ?? new Error('Player configuration is unavailable');
    return this.controller.start(machine);
  }
  async render() {
    const revision = ++this.revision;
    this.initializationError = null;
    this.download?.abort();
    const download = new AbortController();
    this.download = download;
    const previous = this.controller;
    this.controller = null;
    await this.cleanup;
    await previous?.dispose();
    if (!this.isConnected || revision !== this.revision) return;
    try {
      const response = await fetch(new URL('../player.html', import.meta.url), { signal: download.signal });
      if (!response.ok) throw new Error(`Player download failed: HTTP ${response.status}`);
      const markup = await response.text();
      if (revision !== this.revision || !this.isConnected) return;
      this.shadowRoot.innerHTML = markup;
      for (const image of this.shadowRoot.querySelectorAll('img[src]')) image.src = new URL(`../${image.getAttribute('src')}`, import.meta.url).href;
      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = new URL('../style.css', import.meta.url).href;
      const styled = new Promise((resolve, reject) => {
        stylesheet.onload = resolve;
        stylesheet.onerror = () => reject(new Error('Player stylesheet failed to load'));
      });
      this.shadowRoot.prepend(stylesheet);
      await styled;
      if (revision !== this.revision || !this.isConnected) return;
      const url = this.getAttribute('config');
      if (!url) throw new Error('Set the config attribute to the player configuration URL');
      this.controller = mountPlayer(this.shadowRoot, new URL(url, this.ownerDocument.baseURI).href);
      await this.controller.ready;
    } catch (error) {
      if (revision !== this.revision || !this.isConnected) return;
      this.initializationError = error;
      const message = this.shadowRoot.getElementById('status') ?? document.createElement('p');
      message.textContent = error.message;
      message.setAttribute('role', 'status');
      message.classList.add('error');
      if (!message.isConnected) this.shadowRoot.append(message);
    }
  }
}

if (!customElements.get('bizmuth-player')) customElements.define('bizmuth-player', BizmuthPlayer);
